import 'twin.macro';
import React, { useEffect, useRef, useState } from 'react';
import { getFicheDePrep } from '@teetsh/app/src/queries/fdp/useFicheDePrep';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { useSelectProgItem } from './useSelectProgItem';
import { getSequence } from 'queries/sequences/useSequence';
import {
  useCloseProgItemDrawer,
  useIsProgItemDrawerOpen,
  useSetProgItemPayload,
} from 'containers/CahierJournal/LinkProgItemDrawer/atoms';
import { useSelectFicheDePrep } from 'containers/CahierJournal/EventModal/ModalBody/Classic/LinkedFichesDePreps/useSelectFicheDePrep';
import {
  ArrowTopRightOnSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useField } from 'react-final-form';
import ButtonExternal from 'components/Buttons/ExternalLink';
import { useProgrammationItemHierarchy } from 'containers/Programmations/atoms/useProgrammationItemHierarchy';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { ProgressionIcon } from 'components/Icons/icons/ProgressionIcon';

interface ProgItemProps {
  itemId: string;
  onDelete: () => void;
}

function ProgrammationElementDetails(props: ProgItemProps) {
  const { itemId, onDelete } = props;
  const { isLoading, data } = useProgrammationItemHierarchy({
    itemId,
  });
  const { school, schoolyear } = useSchoolSchoolyear();
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div tw="flex items-center gap-x-2">
        <div tw="px-4 py-3 flex-1 rounded-lg bg-neutral-quiet">
          <div className="tsh-skeleton" tw="h-5 w-full" />
        </div>
        <div tw="w-9 h-9">
          <div className="tsh-skeleton" tw="w-9 h-9 rounded-lg" />
        </div>
        <div tw="w-9 h-9">
          <div className="tsh-skeleton" tw="w-9 h-9 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div tw="flex items-center gap-x-2">
      <div
        tw="px-4 py-3 flex-1 rounded-lg bg-neutral-quiet font-semibold relative transition-all ease-in-out duration-200 overflow-hidden"
        css={[
          css`
            interpolate-size: allow-keywords; // allows animation from/to auto
          `,
          !expanded && tw`h-14`,
        ]}
      >
        <div dangerouslySetInnerHTML={{ __html: data?.itemValue || '' }} />
        <div
          tw="absolute inset-0 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        />
        <div
          css={[
            tw`absolute bottom-0 left-0 right-0 h-6 flex items-end justify-center pointer-events-none`,
            !expanded &&
              tw`bg-[linear-gradient(to_bottom,_rgba(242,_242,_242,_0)_0%,_rgba(242,_242,_242,_0.9)_100%)]`,
          ]}
        >
          {expanded ? (
            <ChevronUpIcon tw="h-5 w-5 text-neutral-intense" />
          ) : (
            <ChevronDownIcon tw="h-5 w-5 text-neutral-intense" />
          )}
        </div>
      </div>
      <ButtonExternal
        minimal
        intent="none"
        href={`/annee-scolaire/${schoolyear.id}/ecole/${school.id}/programmations/item-redirect/${itemId}`}
        icon={ArrowTopRightOnSquareIcon}
      />
      <Button minimal intent="danger" icon={TrashIcon} onClick={onDelete} />
    </div>
  );
}

interface Props {
  programmationItemId?: string;
  onChange: (v: string | undefined) => void;
}

export function LinkedProgItem(props: Props) {
  const { programmationItemId, onChange } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const onSelectProgItem = useSelectProgItem();
  const setProgItemPayload = useSetProgItemPayload();
  const close = useCloseProgItemDrawer();
  const onSelectFicheDePrep = useSelectFicheDePrep();
  const linkedFichesDePrepField = useField('linkedFichesDePrep');
  const isDrawerOpen = useIsProgItemDrawerOpen();
  const { t } = useTranslation(['cahierJournal']);

  useEffect(() => {
    setProgItemPayload({
      callback: progItem => {
        onChange(progItem.id);
        close();
        setTimeout(() => {
          divRef?.current?.focus();
        }, 1);
      },
      onSelect: (progItem, matiere, domaine, _, doNotPrefill) => {
        onSelectProgItem(progItem, matiere, domaine);
        if (progItem.ficheDePrep && !doNotPrefill) {
          return getFicheDePrep(
            progItem.ficheDePrep.sequenceId,
            progItem.ficheDePrep.id,
          ).then(ficheDePrep => {
            getSequence(progItem?.ficheDePrep?.sequenceId).then(sequence =>
              onSelectFicheDePrep(ficheDePrep, sequence.title),
            );
            linkedFichesDePrepField.input.onChange([
              ...(linkedFichesDePrepField.input.value || []).filter(
                l => l.id !== ficheDePrep.id,
              ),
              {
                id: ficheDePrep.id,
                label: ficheDePrep.title,
                sequenceId: ficheDePrep.sequenceId,
              },
            ]);
            return ficheDePrep;
          });
        }
        return Promise.resolve(undefined);
      },
      isOpen: isDrawerOpen,
    });
  }, [onChange, divRef, programmationItemId]);

  if (!programmationItemId) {
    return null;
  }
  return (
    <div ref={divRef}>
      <h4 tw="text-neutral-moderate mb-2 flex items-center gap-1">
        <ProgressionIcon tw="inline-block h-5 w-5" />
        {t('cahierJournal:seance.modal.progressionElement')}
      </h4>
      <ProgrammationElementDetails
        itemId={programmationItemId}
        onDelete={() => onChange(undefined)}
      />
    </div>
  );
}
